import { useEffect } from 'react';
import {
    Divider,
    useAppDispatch,
    useAppSelector,
    useTheme,
    Stack,
    Skeleton,
} from '../../../base';
import { reportsActions, reportsSelectors } from '../../../actions';
import { ReportPeriod } from '../../../common';
import {
    IReportsFilterValue,
    IFetchTimeToHireParams,
} from '../../../common/interfaces/reports.interfaces';

import ReportsFilter from '../ReportsFilter';
import CompanyWideView from './CompanyWideView';
import JobSpecificView from './JobSpecificView';
import JobComparisonView from './JobComparisonView';
import { StyledTimeToHireWrapper } from './TimeToHire.styles';

const TimeToHire = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const timeToHireData = useAppSelector(reportsSelectors.getTimeToHireData);
    const isLoading = useAppSelector(reportsSelectors.getIsLoadingTimeToHire);

    useEffect(() => {
        dispatch(
            reportsActions.fetchTimeToHire({
                time_frame: 'lifetime',
            })
        );
    }, [dispatch]);

    const handleFilterChange = (filterValue: IReportsFilterValue) => {
        const params: IFetchTimeToHireParams = {};

        if (filterValue.jobIds && filterValue.jobIds.length > 0) {
            params.job_ids = filterValue.jobIds.join(',');
        } else if (filterValue.jobId) {
            params.job_ids = String(filterValue.jobId);
        }

        if (
            filterValue.timeFrame === ReportPeriod.CUSTOM &&
            filterValue.customDateRange
        ) {
            params.time_frame = 'custom';
            params.custom_from = filterValue.customDateRange.fromDate;
            params.custom_to = filterValue.customDateRange.toDate;
        } else if (filterValue.timeFrame) {
            params.time_frame = filterValue.timeFrame;
        }

        dispatch(reportsActions.fetchTimeToHire(params));
    };

    const renderLoadingSkeleton = () => (
        <Stack sx={{ padding: theme.spacing(2, 3) }} spacing={3}>
            <Stack spacing={1}>
                <Skeleton width={200} height={28} />
                <Skeleton width={350} height={18} />
            </Stack>
            <Skeleton
                variant="rectangular"
                height={400}
                sx={{ borderRadius: 2 }}
            />
        </Stack>
    );

    const renderView = () => {
        if (isLoading) return renderLoadingSkeleton();
        if (!timeToHireData) return null;

        switch (timeToHireData.view) {
            case 'company':
                return <CompanyWideView />;
            case 'job':
                return <JobSpecificView />;
            case 'comparison':
                return <JobComparisonView />;
            default:
                return null;
        }
    };

    return (
        <StyledTimeToHireWrapper>
            <ReportsFilter
                onChange={handleFilterChange}
                defaultTimeFrame={ReportPeriod.LIFETIME}
                maxJobs={3}
                showRecruiterFilter={false}
            />
            <Divider sx={{ borderColor: theme.palette.grey[300] }} />
            {renderView()}
        </StyledTimeToHireWrapper>
    );
};

export default TimeToHire;
